import { CSSProperties } from 'react'
import Card from 'app/frontend/base/components/Card'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import VisualPlan from 'app/frontend/types/custom/VisualPlan'
import { cn } from '@/lib/utils'

type Props = {
  encounters: wowaudit.Encounter[]
  plansByEncounter: Record<number, RaidPlan[]>
  visualsByEncounter: Record<number, VisualPlan[]>
  selectedEncounterId: number | null
  isLoading: boolean
  onSelect: (encounterId: number) => void
}

const planCountLabel = (count: number) =>
  count === 1 ? '1 plan' : `${count} plans`

const EncounterSelectorRow = ({
  encounters,
  plansByEncounter,
  visualsByEncounter,
  selectedEncounterId,
  isLoading,
  onSelect,
}: Props) => {
  return (
    <div
      className='grid gap-2 grid-cols-2 sm:grid-cols-4 lg:grid-cols-[repeat(var(--cols),minmax(0,1fr))]'
      style={{ '--cols': encounters.length } as CSSProperties}
    >
      {encounters.map((encounter) => {
        const totalCount =
          (plansByEncounter[encounter.id]?.length ?? 0) +
          (visualsByEncounter[encounter.id]?.length ?? 0)
        const selected = encounter.id === selectedEncounterId

        return (
          <Card
            key={encounter.id}
            role='button'
            tabIndex={0}
            onClick={() => onSelect(encounter.id)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(encounter.id)
              }
            }}
            className={cn(
              'p-0 flex items-stretch min-w-0 overflow-hidden cursor-pointer transition-colors hover:border-primary/50',
              selected && 'border-primary hover:border-primary',
            )}
          >
            {encounter.thumbnailUrl ? (
              <img
                className='w-10 self-stretch object-cover shrink-0'
                src={encounter.thumbnailUrl}
                alt=''
              />
            ) : (
              <div className='w-10 self-stretch bg-muted shrink-0' />
            )}
            <div className='min-w-0 flex-1 px-2 py-1 flex flex-col justify-center gap-0.5 leading-tight'>
              <div className='truncate font-medium text-sm leading-tight'>
                {encounter.name}
              </div>
              <div
                className={cn(
                  'text-sm text-muted-foreground leading-tight truncate transition-opacity duration-300',
                  isLoading ? 'opacity-0' : 'opacity-100',
                )}
              >
                {planCountLabel(totalCount)}
              </div>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

export default EncounterSelectorRow
